/**
 * M7 S32: the Logs screen's controller and its dashboard section (Log › Case file).
 *
 * - **Story source** (`LogsStorySource`): what the screen reads and sends. The Career is read as it is (the story
 *   block is the truth, spec §1 rule 11); marking a log read and setting a lead's nav target go back through the
 *   story link (S30), which owns the Career's story commands and resolves a lead's `systemHint`/`poiKind` to a
 *   `setNavTarget` (or a route) in the Reach.
 * - **Controller** (`createLogsController`, Node-tested): builds the `LogsView`, runs `reduceLogs` for every action
 *   and carries out its effects.
 * - **Section** (`createLogsSection`, dashboard spec §2.2, card M3): the Log › Case file body. The dashboard shell owns
 *   the frame, Close, the hints, Esc and the keys; the shell's focus inputs (arrows/WASD, Enter) reach the screen
 *   through the section's `nav` port. It refreshes on mount and follows the story's changes while mounted.
 *
 * UI events only.
 */
import { useEffect, useState, type ReactNode } from 'react'
import type { Career } from '../../career/types'
import { logger } from '../../core/logger'
import type { DashFocusInput } from '../dashboard/focus'
import type { DashSectionComponent, DashSectionProps } from '../dashboard/registry'
import type { NavInput } from '../strategic/mount'
import { ClaimPanel } from './ClaimPanel'
import { createFinaleController, type FinaleController, type LogsFinaleSource } from './finaleHost'
import { FINALE_SCREEN } from './models/finale'
import { Logs } from './Logs'
import {
  buildClaimView,
  clampClaim,
  createClaimState,
  draftTier,
  loadClaimPanelConfig,
  reduceClaim,
  type ClaimAction,
  type ClaimPanelConfig,
  type ClaimRecipient,
  type ClaimState,
  type ClaimView,
} from './models/claim'
import type { LogsConfig } from './models/config'
import { buildLogsView, type LogMessage, type LogsData, type LogsView } from './models/logs'
import type { LogsNames } from './models/records'
import { clampState, createLogsState, reduceLogs, type LogsAction, type LogsEffect, type LogsScreenState } from './models/screen'

/** M7 S34: what the claim panel reads and sends (the story link provides it). */
export interface ClaimSource {
  /** The worker's faction day (the retry rule's day). */
  day(): number
  /** Who takes `tier`'s claim: the framed recipient, or its comms fallback when the dock is refused. */
  recipient(tier: number): ClaimRecipient
  /** States the claim through the story link; null when it was refused (nothing changed). */
  submit(tier: number, words: readonly string[]): { correct: boolean } | null
}

/** What the Logs screen reads and sends (the story link provides it; S30's `ServiceMap.story`). */
export interface LogsStorySource {
  /** The current Career (null outside a career). */
  career(): Career | null
  data(): LogsData
  names: LogsNames
  /** Token values for a template log or a corroboration instance (`{pilot}`, `{day}` ...). */
  vars?(logId: string): Readonly<Record<string, string>> | undefined
  /** The S11 `log` callbacks kept so far, oldest first. */
  messages?(): readonly LogMessage[]
  /** The player corporation's tetherstone sold to Tethys refineries (null: no corporation). */
  corpShare?(): { corp: string; tonnes: number } | null
  /** Opens a log in the Career (`story/logs.ts readLog`): it stops being unread. */
  markRead?(logId: string): void
  /** Sets the nav target for the lead `logId` opened; false when there is no fix on it yet. */
  setLeadTarget?(logId: string): boolean
  /** M7 S34: the claim panel's story side (absent: no claim draft row). */
  claim?: ClaimSource
  /** M7 S39: the finale flow (absent: no finale row). */
  finale?: LogsFinaleSource
  /** S37: a log's generated table rows (undefined = the data's). */
  rows?(logId: string): readonly (readonly string[])[] | undefined
  /** Calls `fn` when the story changes (a log picked up, a claim made); returns the unsubscribe. */
  onChange?(fn: () => void): () => void
}

export interface LogsControllerDeps {
  source: LogsStorySource
  config: LogsConfig
  /** M7 S34: the claim panel's words (default `claimPanel.json`). */
  claimConfig?: ClaimPanelConfig
  onClose?: () => void
}

/** M7 S34: the open claim panel. */
export interface ClaimPanelOpen {
  view: ClaimView
  state: ClaimState
}

export interface LogsController {
  readonly state: LogsScreenState
  /** The view; null outside a career. */
  readonly view: LogsView | null
  /** M7 S34: the claim panel, while it is open. */
  readonly claim: ClaimPanelOpen | null
  /** M7 S39: the Finale screen, while it is open. */
  readonly finale: FinaleController | null
  /** M7 S39: opens the Finale screen (the finale row, the debug hook); false when no path is on offer. */
  openFinale(): boolean
  dispatch(a: LogsAction): void
  /** M7 S34: an action on the open claim panel. */
  claimDispatch(a: ClaimAction): void
  /** Re-reads the Career and rebuilds the view. */
  refresh(): void
  onChange(fn: () => void): () => void
}

export function createLogsController(deps: LogsControllerDeps): LogsController {
  let state = createLogsState()
  let view: LogsView | null = null
  let claim: ClaimPanelOpen | null = null
  let finale: FinaleController | null = null
  const claimCfg = deps.claimConfig ?? loadClaimPanelConfig()
  const listeners = new Set<() => void>()
  const src = deps.source

  function changed(): void {
    for (const fn of [...listeners]) fn()
  }

  function build(): LogsView | null {
    const career = src.career()
    if (career === null) return null
    const built = buildLogsView({
      career,
      data: src.data(),
      config: deps.config,
      names: src.names,
      vars: src.vars === undefined ? undefined : (id) => src.vars?.(id),
      messages: src.messages?.() ?? [],
      corpShare: src.corpShare?.() ?? null,
      rows: src.rows === undefined ? undefined : (id) => src.rows?.(id),
    })
    const draft = src.claim !== undefined && draftTier(career, src.data()) !== null
    const withDraft = draft ? { ...built, claimDraft: claimCfg.claim.draftRow } : built
    return src.finale?.open() === true ? { ...withDraft, finaleRow: FINALE_SCREEN.logsRow } : withDraft
  }

  function buildClaim(): ClaimView | null {
    const career = src.career()
    const cs = src.claim
    if (career === null || cs === undefined) return null
    const tier = draftTier(career, src.data())
    if (tier === null) return null
    return buildClaimView({ career, data: src.data(), config: claimCfg, day: cs.day(), recipient: cs.recipient(tier) })
  }

  /** Rebuilds the open claim panel; after a correct claim (no draft any more) it stays open on its answer. */
  function refreshClaim(): void {
    if (claim === null) return
    const v = buildClaim()
    if (v !== null) claim = { view: v, state: clampClaim(claim.state, v) }
  }

  function claimStep(a: ClaimAction): void {
    if (claim === null) return
    const wrong = src.data().claims.feedback.wrong
    const r = reduceClaim(claim.state, a, claim.view, claimCfg, wrong)
    claim = { ...claim, state: r.state }
    const e = r.effect
    if (e?.kind === 'close') {
      claim = null
      controller.refresh()
      return
    }
    if (e?.kind === 'submit') {
      let out: { correct: boolean } | null = null
      try {
        out = src.claim?.submit(e.tier, e.words) ?? null
      } catch (err: unknown) {
        logger.warn('Logs screen: could not state a claim', { tier: e.tier, err })
      }
      if (out !== null) claim = { ...claim, state: reduceClaim(claim.state, { type: 'result', correct: out.correct }, claim.view, claimCfg, wrong).state }
      controller.refresh()
      return
    }
    changed()
  }

  function openFinale(): boolean {
    const f = src.finale
    if (f === undefined || src.career() === null || !f.open()) return false
    finale = createFinaleController({
      finale: f,
      career: () => src.career(),
      bundleConfig: claimCfg,
      onChange: changed,
      onClose: () => {
        finale = null
        controller.refresh()
      },
    })
    changed()
    return true
  }

  function run(e: LogsEffect): void {
    switch (e.kind) {
      case 'close':
        deps.onClose?.()
        return
      case 'read':
        try {
          src.markRead?.(e.logId)
        } catch (err: unknown) {
          logger.warn('Logs screen: could not mark a log read', { log: e.logId, err })
        }
        controller.refresh()
        return
      case 'finale':
        openFinale()
        return
      case 'claim': {
        const v = buildClaim()
        if (v !== null) claim = { view: v, state: createClaimState(v) }
        changed()
        return
      }
      case 'navTarget': {
        let ok = false
        try {
          ok = src.setLeadTarget?.(e.logId) ?? false
        } catch (err: unknown) {
          logger.warn('Logs screen: could not set a lead nav target', { log: e.logId, err })
        }
        step({ type: 'notice', text: ok ? deps.config.leads.navSet : deps.config.leads.navUnavailable })
        return
      }
    }
  }

  function step(a: LogsAction): void {
    if (view === null) return
    if (finale !== null && a.type === 'nav') {
      finale.input(a.input)
      return
    }
    if (claim !== null && a.type === 'nav') {
      claimStep(a)
      return
    }
    const r = reduceLogs(state, a, view)
    state = r.state
    changed()
    if (r.effect !== null) run(r.effect)
  }

  const controller: LogsController = {
    get state(): LogsScreenState {
      return state
    },
    get view(): LogsView | null {
      return view
    },
    get claim(): ClaimPanelOpen | null {
      return claim
    },
    get finale(): FinaleController | null {
      return finale
    },
    openFinale,
    dispatch: step,
    claimDispatch: claimStep,
    refresh(): void {
      try {
        view = build()
      } catch (err: unknown) {
        logger.warn('Logs screen: could not build the logs', err)
        view = null
      }
      if (view !== null) state = clampState(state, view)
      refreshClaim()
      finale?.refresh()
      changed()
    },
    onChange(fn: () => void): () => void {
      listeners.add(fn)
      return () => {
        listeners.delete(fn)
      }
    },
  }
  return controller
}

// ---------------------------------------------------------------------------------------------
// The dashboard section (Log › Case file; dashboard spec §2.2, card M3)
// ---------------------------------------------------------------------------------------------

export interface LogsSectionOptions {
  source: LogsStorySource
  config: LogsConfig
  /** M7 S34: the claim panel's words (default `claimPanel.json`). */
  claimConfig?: ClaimPanelConfig
}

export interface LogsSection {
  readonly controller: LogsController
  /** The section body the dashboard renders (`registerDashSection`). */
  readonly component: DashSectionComponent
}

/** The shell's focus inputs the Logs screen takes (back stays the shell's: Esc and Backspace go a level out). */
const NAV_OF: Readonly<Partial<Record<DashFocusInput, NavInput>>> = Object.freeze({
  up: 'up',
  down: 'down',
  left: 'left',
  right: 'right',
  activate: 'activate',
})

/**
 * Builds the Logs screen as a dashboard section: the controller (shared with the debug hooks) and a component that
 * refreshes on mount, follows the story's changes while mounted and takes the shell's focus inputs. The shell owns
 * the frame, Close and the hints; the screen's own Close is gone (the `close` effect of Back at the top does nothing).
 */
export function createLogsSection(opts: LogsSectionOptions): LogsSection {
  const claimConfig = opts.claimConfig ?? loadClaimPanelConfig()
  const controller = createLogsController({ source: opts.source, config: opts.config, claimConfig })

  function LogsSectionBody(p: DashSectionProps): ReactNode {
    const [, setTick] = useState(0)
    useEffect(() => {
      const stops = [controller.onChange(() => setTick((n) => n + 1))]
      const off = opts.source.onChange?.(() => controller.refresh())
      if (off !== undefined) stops.push(off)
      controller.refresh()
      return () => {
        for (const s of stops) s()
      }
    }, [])
    useEffect(
      () =>
        p.nav.subscribe((input) => {
          const n = NAV_OF[input]
          if (n === undefined || controller.view === null) return false
          controller.dispatch({ type: 'nav', input: n })
          return true
        }),
      [p.nav],
    )
    const view = controller.view
    if (view === null) return null
    const f = controller.finale
    if (f !== null) return f.node()
    const open = controller.claim
    if (open !== null) {
      return (
        <div className="sui lg-screen" data-testid="logs-screen" aria-label={view.title}>
          <ClaimPanel view={open.view} state={open.state} config={claimConfig} dispatch={controller.claimDispatch} />
        </div>
      )
    }
    return <Logs view={view} state={controller.state} config={opts.config} dispatch={controller.dispatch} embedded />
  }

  return { controller, component: LogsSectionBody }
}
